import { useAuth } from "@/contexts/AuthContext";
import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { 
  User, 
  Mail, 
  Calendar,
  Shield,
  Bot,
  Key
} from "lucide-react";

export default function Settings() {
  const { user } = useAuth();

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString('fr-CH', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const currentYear = new Date().getFullYear();

  return (
    <Layout>
      <div className="max-w-3xl space-y-6 fade-in">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-gray-900">
            Paramètres
          </h1>
          <p className="text-gray-500 mt-1">
            Gérez votre compte et vos préférences
          </p>
        </div>

        {/* Profile */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5 text-[#1E8B5A]" />
              Profil utilisateur
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="h-16 w-16 rounded-full bg-[#1E8B5A] flex items-center justify-center text-white text-2xl font-bold">
                {user?.name?.charAt(0).toUpperCase() || "U"}
              </div>
              <div>
                <p className="text-lg font-semibold text-gray-900">{user?.name}</p>
                <p className="text-gray-500">{user?.email}</p>
              </div>
            </div>
            
            <Separator />
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nom</Label>
                <Input value={user?.name || ""} disabled />
              </div>
              <div className="space-y-2">
                <Label>Adresse e-mail</Label>
                <Input value={user?.email || ""} disabled />
              </div>
            </div>
            
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Calendar className="h-4 w-4" />
              Compte créé le {formatDate(user?.created_at)}
            </div>
          </CardContent>
        </Card>

        {/* Moteurs de recherche */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bot className="h-5 w-5 text-[#1E8B5A]" />
              Moteurs de chiffrage configurés
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-4 rounded-xl border border-gray-200 bg-purple-50/50">
                <div className="flex items-center gap-2 mb-2">
                  <span className="w-2 h-2 rounded-full bg-purple-500" />
                  <span className="font-medium text-gray-900">Modèle avancé</span>
                </div>
                <p className="text-sm text-gray-600">Extraction précise</p>
                <p className="text-xs text-green-600 mt-2">✓ Configuré</p>
              </div>
              
              <div className="p-4 rounded-xl border border-gray-200 bg-green-50/50">
                <div className="flex items-center gap-2 mb-2">
                  <span className="w-2 h-2 rounded-full bg-green-500" />
                  <span className="font-medium text-gray-900">Modèle standard</span>
                </div>
                <p className="text-sm text-gray-600">Polyvalent</p>
                <p className="text-xs text-green-600 mt-2">✓ Configuré</p>
              </div>
              
              <div className="p-4 rounded-xl border border-gray-200 bg-blue-50/50">
                <div className="flex items-center gap-2 mb-2">
                  <span className="w-2 h-2 rounded-full bg-blue-500" />
                  <span className="font-medium text-gray-900">Modèle rapide</span>
                </div>
                <p className="text-sm text-gray-600">Recherche web</p>
                <p className="text-xs text-green-600 mt-2">✓ Configuré</p>
              </div>
            </div>
            
            <p className="text-sm text-gray-500">
              Les clés d'accès sont configurées côté serveur. Contactez l'administrateur pour les modifier.
            </p>
          </CardContent>
        </Card>

        {/* Security */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-[#1E8B5A]" />
              Sécurité
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-4 rounded-xl bg-gray-50">
              <div className="flex items-center gap-3">
                <Key className="h-5 w-5 text-gray-500" />
                <div>
                  <p className="font-medium text-gray-900">Authentification</p>
                  <p className="text-sm text-gray-500">JWT avec jetons sécurisés</p>
                </div>
              </div>
              <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                Actif
              </span>
            </div>
            
            <div className="flex items-center justify-between p-4 rounded-xl bg-gray-50">
              <div className="flex items-center gap-3">
                <Mail className="h-5 w-5 text-gray-500" />
                <div>
                  <p className="font-medium text-gray-900">Adresse e-mail vérifiée</p>
                  <p className="text-sm text-gray-500">{user?.email}</p>
                </div>
              </div>
              <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                Vérifiée
              </span>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
